import { useForm } from '@tanstack/react-form'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'
import { useEffect } from 'react'

import { CreateDiscountRequestSchema, UpdateDiscountRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { DiscountTypeEnum } from '@/gen/cortado/v1/settings_pb'
import type { Discount } from '@/gen/cortado/v1/invoices_pb'
import { DiscountSchema } from '@/gen/cortado/v1/invoices_pb'
import { useCreateDiscount, useUpdateDiscount } from '@/stores/organization.store'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'

interface DiscountCreateSheetProps {
  organizationId: string
  discount: Discount | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: (discount: Discount, saveForFutureUse: boolean) => void
  /** Hide the "Save for Future Use" checkbox. When hidden, defaults to true. */
  hideSaveForFutureUse?: boolean
}

/**
 * DiscountCreateSheet - Sheet component for creating and editing discounts
 *
 * Features:
 * - Create new discount or edit existing discount
 * - Supports name, description, type (Percentage/Fixed), value, and active status
 * - "Save for Future Use" checkbox controls whether discount is saved to database (create mode only)
 * - When saved: Creates/updates discount via RPC, appears in future dropdowns
 * - When not saved (create only): Returns discount data for inline application to quote only
 * - Uses TanStack Form for form state management
 * - Input validation based on discount type (Percentage: 0-100, Fixed: >= 0)
 * - Toast notifications for success/error
 * - Resets form on close
 *
 * @param organizationId - The organization ID to create/edit the discount for
 * @param discount - The discount to edit (null for create mode)
 * @param open - Control sheet visibility
 * @param onOpenChange - Handle open/close state changes
 * @param onSuccess - Callback with created/updated discount and saveForFutureUse flag
 */
export function DiscountCreateSheet({
  organizationId,
  discount,
  open,
  onOpenChange,
  onSuccess,
  hideSaveForFutureUse = false,
}: DiscountCreateSheetProps) {
  const { mutation: createMutation } = useCreateDiscount()
  const { mutation: updateMutation } = useUpdateDiscount()

  const isEditMode = !!discount
  const isCreateMode = !isEditMode

  // Setup TanStack Form
  const form = useForm({
    defaultValues: {
      name: discount?.name ?? '',
      description: discount?.description ?? '',
      discountType: discount?.discountType ?? DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE,
      value: discount?.value ?? 0,
      isActive: discount?.isActive ?? true,
      saveForFutureUse: hideSaveForFutureUse ? true : true,
    },
    onSubmit: async ({ value }) => {
      try {
        // Validate inputs
        if (!value.name.trim()) {
          toast.error('Please enter a discount name')
          return
        }
        if (value.discountType === DiscountTypeEnum.DISCOUNT_TYPE_UNKNOWN) {
          toast.error('Please select a discount type')
          return
        }

        // Validate value based on type
        if (value.discountType === DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE) {
          if (value.value <= 0 || value.value > 100) {
            toast.error('Percentage must be greater than 0 and less than or equal to 100')
            return
          }
        } else if (value.discountType === DiscountTypeEnum.DISCOUNT_TYPE_FIXED) {
          if (value.value <= 0) {
            toast.error('Fixed amount must be greater than 0')
            return
          }
        }

        if (isEditMode && discount) {
          // Update existing discount
          const request = create(UpdateDiscountRequestSchema, {
            id: discount.id,
            name: value.name.trim(),
            description: value.description.trim(),
            discountType: value.discountType,
            value: value.value,
            isActive: value.isActive,
            updateMask: create(FieldMaskSchema, {
              paths: ['name', 'description', 'discount_type', 'value', 'is_active'],
            }),
          })

          const response = await updateMutation.mutateAsync(request)

          if (response.discount) {
            toast.success('Discount updated successfully')

            // Call success callback with updated discount
            if (onSuccess) {
              onSuccess(response.discount, true)
            }
          }

          onOpenChange(false)
          form.reset()
        } else {
          // Create mode
          // When hideSaveForFutureUse is true, always save to database
          const shouldSave = hideSaveForFutureUse || value.saveForFutureUse

          if (shouldSave) {
            // Save to database via CreateDiscount RPC
            const request = create(CreateDiscountRequestSchema, {
              organizationId,
              name: value.name.trim(),
              description: value.description.trim(),
              discountType: value.discountType,
              value: value.value,
              isActive: true, // Saved discounts are active by default
            })

            const response = await createMutation.mutateAsync(request)

            if (response.discount) {
              toast.success('Discount saved for future use')

              // Call success callback with saved discount
              if (onSuccess) {
                onSuccess(response.discount, true)
              }
            }
          } else {
            // Return inline discount data without saving
            // Create a discount object for inline use (no API call)
            const inlineDiscount = create(DiscountSchema, {
              id: '00000000-0000-0000-0000-000000000000', // Nil UUID for inline discounts
              organizationId,
              name: value.name.trim(),
              description: value.description.trim(),
              discountType: value.discountType,
              value: value.value,
              isActive: false, // Not saved, so not "active" in dropdown sense
              createdBy: '',
            })

            toast.success('Discount ready to apply')

            // Call success callback with inline discount
            if (onSuccess) {
              onSuccess(inlineDiscount, false)
            }
          }

          onOpenChange(false)
          form.reset()
        }
      } catch (error) {
        console.error('Failed to save discount:', error)
        reportError(error, { feature: 'discount_save', kind: 'mutation' })
        toast.error(`Failed to ${isEditMode ? 'update' : 'create'} discount`, {
          description: getErrorDescription(error),
        })
      }
    },
  })

  // Reset form when discount or open state changes
  useEffect(() => {
    if (open) {
      form.reset({
        name: discount?.name ?? '',
        description: discount?.description ?? '',
        discountType: discount?.discountType ?? DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE,
        value: discount?.value ?? 0,
        isActive: discount?.isActive ?? true,
        saveForFutureUse: hideSaveForFutureUse ? true : true,
      })
    }
  }, [discount, open, hideSaveForFutureUse])

  const mutation = isEditMode ? updateMutation : createMutation

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{isEditMode ? 'Edit Discount' : 'Create New Discount'}</SheetTitle>
          <SheetDescription>
            {isEditMode
              ? 'Update the discount details below.'
              : 'Add a discount to apply to this quote.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id="discount-form"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="grid auto-rows-min gap-6"
          >
          <form.Field name="name">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="discount-name">
                  Discount Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="discount-name"
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="e.g., Early Bird, Bulk Order"
                  disabled={mutation.isPending}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="description">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="discount-description">Description (Optional)</Label>
                <Textarea
                  id="discount-description"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="Brief description of when this discount applies"
                  disabled={mutation.isPending}
                  rows={2}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="discountType">
            {(field) => (
              <div className="grid gap-3">
                <Label htmlFor="discount-type">Discount Type</Label>
                <Select
                  value={String(field.state.value)}
                  onValueChange={(value) => {
                    field.handleChange(Number(value) as DiscountTypeEnum)
                    // Reset value to 0 when discount type changes
                    form.setFieldValue('value', 0)
                  }}
                  disabled={mutation.isPending}
                >
                  <SelectTrigger id="discount-type">
                    <SelectValue placeholder="Select discount type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE)}>
                      Percentage
                    </SelectItem>
                    <SelectItem value={String(DiscountTypeEnum.DISCOUNT_TYPE_FIXED)}>
                      Fixed Amount
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="value">
            {(field) => {
              const discountType = form.state.values.discountType
              const isPercentage = discountType === DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE

              return (
                <div className="grid gap-3">
                  <Label htmlFor="discount-value">
                    {isPercentage ? 'Percentage (%)' : 'Fixed Amount ($)'} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="discount-value"
                    type="number"
                    min="0"
                    max={isPercentage ? "100" : undefined}
                    step={isPercentage ? "0.01" : "0.01"}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(Number(e.target.value) || 0)}
                    onBlur={(e) => {
                      const value = Number(e.target.value) || 0
                      // Enforce validation based on type
                      const clampedValue = isPercentage ? Math.max(0, Math.min(100, value)) : Math.max(0, value)
                      field.handleChange(clampedValue)
                      field.handleBlur()
                    }}
                    disabled={mutation.isPending}
                    placeholder={isPercentage ? "0.00" : "0.00"}
                  />
                  <p className="text-sm text-muted-foreground">
                    {isPercentage
                      ? 'Must be greater than 0 and less than or equal to 100'
                      : 'Must be greater than 0'}
                  </p>
                </div>
              )
            }}
          </form.Field>

          {isEditMode && (
            <form.Field name="isActive">
              {(field) => (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is-active"
                    checked={field.state.value}
                    onCheckedChange={(checked) => field.handleChange(!!checked)}
                    disabled={mutation.isPending}
                  />
                  <Label
                    htmlFor="is-active"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Active
                  </Label>
                </div>
              )}
            </form.Field>
          )}

          {!hideSaveForFutureUse && isCreateMode && (
            <>
              <form.Field name="saveForFutureUse">
                {(field) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="save-for-future"
                      checked={field.state.value}
                      onCheckedChange={(checked) => field.handleChange(!!checked)}
                      disabled={mutation.isPending}
                    />
                    <Label
                      htmlFor="save-for-future"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Save for future use
                    </Label>
                  </div>
                )}
              </form.Field>

              <p className="text-sm text-muted-foreground -mt-3">
                {form.state.values.saveForFutureUse
                  ? 'This discount will be saved and appear in your discounts list for future quotes.'
                  : 'This discount will only be applied to this quote and not saved.'}
              </p>
            </>
          )}

          </form>
        </SheetBody>
        <SheetFooter>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </SheetClose>
          <form.Subscribe selector={(s) => [s.values, s.isSubmitting] as const}>
            {([values, isSubmitting]) => {
              const disabled =
                mutation.isPending ||
                isSubmitting ||
                !values.name.trim() ||
                !values.value ||
                values.value <= 0
              return (
                <Button type="submit" form="discount-form" disabled={disabled}>
                  {mutation.isPending
                    ? isEditMode ? 'Updating...' : 'Creating...'
                    : isEditMode ? 'Update Discount' : 'Create Discount'}
                </Button>
              )
            }}
          </form.Subscribe>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
